import {Component, HostListener, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MessageService, TreeNode} from 'primeng/api';
import {TranslocoService} from '@ngneat/transloco';
import {Organization} from '../../../model/organization';
import {OrganizationPersistRequest} from '../../../model/requests/organization-persist-request';
import {OrganizationsService} from '../../../services/organizations.service';
import {ComponentCanDeactivate} from "../../../common/can-deactivate/component-can-deactivate";
import {CommonMessagesService} from "../../../services/common-messages.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {CountryISO, PhoneNumberFormat, SearchCountryField} from 'ngx-intl-tel-input';
import {PhoneService} from "../../../services/phone.service";
import {GeocodingService} from "../../../services/geocoding.service";
import {MapService} from "../../../services/map.service";
import {OrganizationTypesService} from "../../../services/organization-types.service";
import {OrganizationType} from "../../../model/organization-type";
import {HierarchyService} from "../../../services/hierarchy.service";
import {HttpStatusCode} from "@angular/common/http";

@Component({
  selector: 'app-organizations-form',
  templateUrl: 'organizations-form.component.html',
  styleUrls: ['organizations-form.component.scss']
})
export class OrganizationsFormComponent implements OnInit, ComponentCanDeactivate {

  public mode: 'create' | 'edit';

  public preferredCountries: any[];

  public hierarchy: TreeNode[];
  public organizationTypes: OrganizationType[];
  public isLoadingOrganization: boolean = true;
  public selectedParent: any;

  public SearchCountryField = SearchCountryField;
  public PhoneNumberFormat = PhoneNumberFormat;

  public organizationForm: FormGroup;
  public persistingChanges: boolean;

  private language: string;
  private longitude: number;
  private latitude: number;

  private nodes: any[];
  private organization: Organization;
  private formSuccess: boolean;


  constructor(private organizationsService: OrganizationsService,
              private geocodingService: GeocodingService,
              private mapService: MapService,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private messageService: MessageService,
              private commonMessagesService: CommonMessagesService,
              private organizationTypesService: OrganizationTypesService,
              private hierarchyService: HierarchyService,
              private formBuilder: FormBuilder,
              private phoneService: PhoneService) {
    this.preferredCountries = [CountryISO.Spain];
  }

  ngOnInit(): void {
    this.language = this.i18n.getActiveLang();
    this.loadDataAndForm();
  }

  private loadDataAndForm(): void {
    this.isLoadingOrganization = true;
    const promises = [
      this.organizationTypesService.getOrganizationTypes().toPromise().then(response => {
        this.organizationTypes = response.organizationTypes;
      }),
      this.hierarchyService.getHierarchyDropdown().toPromise().then(response => {
        let responseTreeNode = this.hierarchyService.getHierarchyTreeNodeFilter(response.hierarchyDto);
        this.nodes = responseTreeNode[0];
        this.hierarchy = responseTreeNode[1];
      })
    ];

    Promise.all(promises).then(() => {
      this.route.url.subscribe(urlSegment => {
        const firstPath = urlSegment[0].path;
        if (firstPath === 'new') {
          this.mode = 'create';
          this.loadCreationForm();
        } else {
          this.mode = 'edit';
          this.loadEditionForm(firstPath);
        }
      });
    }).finally(() => {
      this.isLoadingOrganization = false;
    });
  }

  setOrganizationLocation(result: any): void {
    this.longitude = result.center[0];
    this.latitude = result.center[1];
    this.organizationForm.get('locality').setValue(result.text);
    result.context.forEach(response => {
      if (response.id.match(/region.*/)) {
        this.organizationForm.get('state').setValue(response.text);
      } else if (response.id.match(/country.*/)) {
        this.organizationForm.get('country').setValue(response.text);
      }
    });
  }

  private loadCreationForm(): void {
    this.organization = new Organization();
    this.createForm(this.organization);
  }

  private loadEditionForm(organizationUuid: string): void {
    this.organizationsService.getOrganization(organizationUuid).toPromise()
      .then(response => {
        this.organization = response.organization;
        this.organization.phone = this.phoneService.parsePhoneNumber(this.organization.phone);
        this.selectedParent = this.nodes[this.organization.parentUuid];
        this.createForm(this.organization);
      }, error => {
        if (error.status === 404) {
          this.router.navigate(['/not-found']);
        }
      });
  }

  private createForm(organization: Organization): void {
    this.organizationForm = this.formBuilder.group({
      orgCode: [organization.orgCode, [Validators.required, Validators.maxLength(255)]],
      name: [organization.name, [Validators.required, Validators.maxLength(255)]],
      email: [organization.email, [Validators.required, Validators.maxLength(255), Validators.email]],
      description: [organization.description, [Validators.maxLength(255)]],
      organizationType: [organization.organizationType?.organizationTypeUuid, [Validators.required,
        Validators.maxLength(255)]],
      address: [organization.address, [Validators.maxLength(255)]],
      phone: [organization.phone, [Validators.maxLength(255)]],
      locality: [{value: organization.geocoding?.locality[this.language], disabled: true}, [Validators.required]],
      state: [{value: organization.geocoding?.state[this.language], disabled: true}, [Validators.required]],
      country: [{value: organization.geocoding?.country[this.language], disabled: true}, [Validators.required]],
      organizationUuid: this.mode === 'edit' ? this.organization.organizationUuid : null
    });
    this.organizationForm.markAsPending();
    this.i18n.selectTranslate('common.search').subscribe(() => {
      setTimeout(() => this.buildGeocoder());
    });
  }

  private buildGeocoder(): void {
    const geocoder = this.mapService.getGeocoder();
    geocoder.addTo('#geocoder');
    geocoder.on('result', (e) => {
      this.setOrganizationLocation(e.result);
    });
    geocoder.on('clear', () => {
      this.resetLocation();
    });
  }

  resetLocation(): void {
    this.organizationForm.get('locality').setValue('');
    this.organizationForm.get('state').setValue('');
    this.organizationForm.get('country').setValue('');
  }

  persistOrganizationData(): void {
    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.organizationsService.createOrganization(persistData).toPromise();
    } else {
      promise = this.organizationsService.editOrganization(persistData).toPromise();
    }

    promise.then(() => {
      if (this.mode === 'create') {
        this.commonMessagesService.showSuccessfullyCreatedMessage();
      } else {
        this.commonMessagesService.showSuccessfullyUpdatedMessage();
      }
      this.formSuccess = true;
      this.router.navigate([`/organizations`]);
    }).catch(error => {
      if (error.status === HttpStatusCode.BadRequest && this.mode === 'edit') {
        const summary = this.i18n.translate('error.' + this.mode + '.generic');
        const detail = this.i18n.translate('error.persist.organizationParent');
        this.messageService.add({severity: 'error', summary, detail, sticky: true});
      } else {
        this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      }
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): OrganizationPersistRequest {
    const formValue = this.organizationForm.getRawValue();
    return {
      orgCode: formValue.orgCode?.trim(),
      name: formValue.name?.trim(),
      email: formValue.email?.trim(),
      description: formValue.description?.trim(),
      organizationTypeUuid: formValue.organizationType,
      parentUuid: this.selectedParent.data,
      address: formValue.address?.trim(),
      longitude: this.longitude,
      latitude: this.latitude,
      phone: formValue.phone ? formValue.phone.e164Number?.trim() : null,
      organizationUuid: this.mode === 'edit' ? this.organization.organizationUuid : null
    };
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || (this.organizationForm?.untouched && this.longitude == null && this.latitude == null
      && this.parentUntouched());
  }

  private parentUntouched(): boolean {
    if (this.mode === 'create') {
      return this.selectedParent == null;
    } else {
      return this.selectedParent?.data === this.organization?.parentUuid;
    }
  }

}
